import React, { useState, useEffect, useRef } from 'react';
import { Attendee } from '@/types/attendee';
import { Card, CardContent } from '@/components/ui/card';
import { formatTimeAgo, formatDateTime } from '../utils/formatUtils';
import { Calendar, ChevronDown, ChevronUp, Phone, Mail, School, Shirt, ToggleRight, CheckCircle, XCircle, Eye, EyeOff, TrendingUp, QrCode } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import CheckInButton from '../CheckInButton';
import PaymentToggle from '../payment/PaymentToggle';
import AttendanceStatus from '../attendance/AttendanceStatus';
import { motion } from 'framer-motion';
import { formatTShirtSize } from '@/utils/formatUtils';
import QRCodeGenerator from '@/components/registration-qr/QRCodeGenerator';
import { formatQRCode } from '@/utils/qrCodeUtils';

interface AttendeeCardProps {
  attendee: Attendee;
  isPaid: boolean;
  isUpdating: boolean;
  localAttendance: boolean;
  localCheckedInAt?: string;
  onCheckInSuccess: (attendee: Attendee, timestamp: string) => void;
  onDirectUpdate: (code: string, updates: any) => Promise<any>;
  onSilentUpdate?: (attendee: Attendee) => void;
  handlePaymentToggleRequest: (checked: boolean) => void;
  showContribution: boolean;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
}

export const AttendeeCard: React.FC<AttendeeCardProps> = ({
  attendee,
  isPaid,
  isUpdating,
  localAttendance,
  localCheckedInAt,
  onCheckInSuccess,
  onDirectUpdate,
  onSilentUpdate,
  handlePaymentToggleRequest,
  showContribution,
  isExpanded = false,
  onToggleExpand = () => {}
}) => {
  const [showPhone, setShowPhone] = useState(false);
  const [showContributionData, setShowContributionData] = useState(false);
  const [effectiveTShirtSize, setEffectiveTShirtSize] = useState<string>('');
  const [showQrCode, setShowQrCode] = useState(false);
  
  useEffect(() => {
    if (attendee?.t_shirt_size) {
      console.log(`AttendeeCard: Updating t-shirt size for ${attendee.code} to ${attendee.t_shirt_size}`);
      setEffectiveTShirtSize(attendee.t_shirt_size);
    }
  }, [attendee, attendee?.t_shirt_size]);

  useEffect(() => {
    const handlePaymentUpdate = (event: CustomEvent) => {
      if (event.detail?.attendeeCode === attendee.code && event.detail?.updatedAttendee) {
        const updatedAttendee = event.detail.updatedAttendee;
        if (updatedAttendee.t_shirt_size) {
          console.log(`AttendeeCard: Payment update event received for ${attendee.code} with t_shirt_size=${updatedAttendee.t_shirt_size}`);
          setEffectiveTShirtSize(updatedAttendee.t_shirt_size);
        }
      }
    };
    
    window.addEventListener('payment-status-updated', handlePaymentUpdate as EventListener);
    
    return () => {
      window.removeEventListener('payment-status-updated', handlePaymentUpdate as EventListener);
    };
  }, [attendee.code]);

  const displayTShirtSize = attendee.t_shirt_size || effectiveTShirtSize;
  const formattedTShirtSize = formatTShirtSize(displayTShirtSize);

  console.log(`AttendeeCard rendering for ${attendee.code}: t_shirt_size=${attendee.t_shirt_size}, effective=${effectiveTShirtSize}, formatted=${formattedTShirtSize}`);

  const sizeClass = formattedTShirtSize.includes('(Nam)') 
    ? 'text-blue-600 dark:text-blue-400 font-medium' 
    : formattedTShirtSize.includes('(Nữ)') 
      ? 'text-pink-600 dark:text-pink-400 font-medium'
      : 'text-gray-600 dark:text-gray-400';

  const enhancedDirectUpdate = async (code: string, updates: any) => {
    const enhancedUpdates = {
      ...updates,
      t_shirt_size: attendee.t_shirt_size || effectiveTShirtSize
    };
    
    console.log(`AttendeeCard: Enhanced update for ${code} with t_shirt_size=${enhancedUpdates.t_shirt_size}`);
    return onDirectUpdate(code, enhancedUpdates);
  };

  const handlePaymentStatusClick = () => {
    handlePaymentToggleRequest(!isPaid);
  };

  const togglePhoneVisibility = () => {
    setShowPhone(!showPhone);
  };

  const toggleContributionVisibility = () => {
    setShowContributionData(!showContributionData);
  };
  
  const toggleQrCodeVisibility = () => {
    setShowQrCode(!showQrCode);
  };

  const getCardGradient = () => {
    if (localAttendance) {
      return 'bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/20';
    } else if (isPaid) {
      return 'bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/20';
    } else {
      return 'bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-950/30 dark:to-yellow-950/20';
    }
  };

  const getBorderColor = () => {
    if (localAttendance) {
      return 'border-l-green-500 dark:border-green-600';
    } else if (isPaid) {
      return 'border-l-blue-500 dark:border-blue-600';
    } else {
      return 'border-l-amber-500 dark:border-amber-600';
    }
  };

  const qrValue = formatQRCode(attendee.code);

  return (
    <motion.div
      whileHover={{ y: -2 }}
      transition={{ duration: 0.2 }}
    >
      <Card className={`mb-3 overflow-hidden border-l-4 shadow-md hover:shadow-lg transition-all duration-200 ${getBorderColor()} ${getCardGradient()}`}>
        <div className="p-4 flex justify-between items-start relative">
          <div className="flex flex-col">
            <div className="font-semibold text-base line-clamp-2 break-words w-full max-w-[200px] leading-tight text-gray-800 dark:text-gray-100">
              {attendee.name}
            </div>
            <div className="text-xs text-muted-foreground flex items-center gap-1.5 mt-1.5">
              <span className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm px-2 py-0.5 rounded-md text-slate-700 dark:text-slate-300 font-mono shadow-sm">
                {attendee.code}
              </span>
            </div>
            
            <div className={`text-xs flex items-center gap-1.5 mt-2 ${sizeClass}`}>
              <Shirt size={14} className="opacity-80" strokeWidth={2} />
              <span className="bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm px-2 py-0.5 rounded shadow-sm">
                {displayTShirtSize ? formattedTShirtSize : "N/A"}
              </span>
            </div>
            
            <div 
              className="text-xs flex items-center gap-1.5 mt-2 cursor-pointer py-1 px-2 rounded-md border border-transparent 
                hover:border-slate-200 dark:hover:border-slate-700 hover:bg-white/50 dark:hover:bg-gray-800/50 
                active:bg-slate-100 dark:active:bg-slate-800 transition-all duration-150 transform active:scale-95 shadow-sm"
              onClick={handlePaymentStatusClick}
              role="button"
              tabIndex={0}
              aria-label={isPaid ? "Click to mark as unpaid" : "Click to mark as paid"}
            >
              {isPaid ? (
                <div className="flex items-center gap-1.5 w-full">
                  <CheckCircle size={14} className="text-green-500" strokeWidth={2.5} />
                  <span className="bg-green-100/80 text-green-700 dark:bg-green-900/40 dark:text-green-400 px-2 py-0.5 rounded-md flex items-center gap-1 backdrop-blur-sm">
                    <ToggleRight size={12} className="text-green-600 dark:text-green-500 mr-0.5" />
                    Đã thanh toán
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 w-full">
                  <XCircle size={14} className="text-red-500" strokeWidth={2.5} />
                  <span className="bg-red-100/80 text-red-700 dark:bg-red-900/40 dark:text-red-400 px-2 py-0.5 rounded-md flex items-center gap-1 backdrop-blur-sm">
                    <ToggleRight size={12} className="text-red-600 dark:text-red-500 mr-0.5" />
                    Chưa thanh toán
                  </span>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <AttendanceStatus 
              isAttended={localAttendance}
              checkedInAt={localCheckedInAt}
              formatDateTime={formatDateTime}
            />
            
            <CheckInButton 
              attendee={{
                ...attendee,
                attended: localAttendance,
                checkedInAt: localCheckedInAt,
                t_shirt_size: displayTShirtSize
              }}
              onSuccess={onCheckInSuccess}
              onDirectUpdate={enhancedDirectUpdate}
              onSilentUpdate={onSilentUpdate}
            />
          </div>
        </div>

        <Collapsible open={isExpanded}>
          <CollapsibleContent>
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
            >
              <CardContent className="pb-4 pt-0 grid grid-cols-1 gap-3 text-sm border-t border-slate-200/70 dark:border-slate-700/70 mt-1 bg-white/30 dark:bg-gray-900/30 backdrop-blur-sm">
                <div className="flex items-center gap-2.5 mt-3">
                  <Mail size={14} className="text-indigo-500 dark:text-indigo-400" />
                  <span className="text-slate-700 dark:text-slate-300 break-all font-medium">{attendee.email || "Chưa cung cấp"}</span>
                </div>
                
                <div className="flex items-center justify-between gap-2.5">
                  <div className="flex items-center gap-2.5">
                    <Phone size={14} className="text-emerald-500 dark:text-emerald-400" />
                    <span className="text-slate-700 dark:text-slate-300 font-medium">
                      {showPhone ? 
                        (attendee.phone || "Chưa cung cấp") : 
                        "••••••••••"
                      }
                    </span>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="p-0 h-9 w-9 rounded-full hover:bg-gray-100/80 dark:hover:bg-gray-800/80 z-10"
                    onClick={togglePhoneVisibility}
                    title={showPhone ? "Ẩn số điện thoại" : "Hiển thị số điện thoại"}
                  >
                    {showPhone ? <EyeOff size={18} /> : <Eye size={18} />}
                  </Button>
                </div>
                
                <div className="flex items-center gap-2.5">
                  <School size={14} className="text-blue-500 dark:text-blue-400" />
                  <span className="text-slate-700 dark:text-slate-300 font-medium">{attendee.class}</span>
                </div>
                
                <div className="flex items-center gap-2.5">
                  <Calendar size={14} className="text-amber-500 dark:text-amber-400" />
                  <span className="text-slate-700 dark:text-slate-300 font-medium" title={formatDateTime(attendee.registeredAt)}>
                    {formatTimeAgo(attendee.registeredAt)}
                  </span>
                </div>
                
                <div className="flex flex-col mt-1 py-2 border-t border-slate-200/70 dark:border-slate-700/70">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600 dark:text-slate-400 font-medium flex items-center gap-1.5">
                      <QrCode size={14} className="text-purple-500" /> 
                      Mã QR:
                    </span>
                    <div className="flex items-center">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-10 px-4 ml-1 rounded-full bg-purple-50 hover:bg-purple-100 border-purple-200 text-purple-700 
                          dark:bg-purple-900/30 dark:hover:bg-purple-800/40 dark:border-purple-700/50 dark:text-purple-400
                          shadow-sm flex items-center gap-1.5 z-10 touch-target"
                        onClick={toggleQrCodeVisibility}
                        title={showQrCode ? "Ẩn mã QR" : "Hiển thị mã QR để test"}
                      >
                        {showQrCode ? (
                          <>
                            <EyeOff size={14} />
                            <span className="text-xs ml-1">Ẩn QR</span>
                          </>
                        ) : (
                          <>
                            <QrCode size={14} className="animate-pulse" />
                            <span className="text-xs ml-1">Xem QR để test</span>
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                  
                  {showQrCode ? (
                    <motion.div 
                      className="mt-2 flex flex-col items-center p-3 bg-white dark:bg-gray-900 rounded-md border border-purple-100 dark:border-purple-800/30 shadow-sm"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="text-xs text-center mb-2 text-purple-600 dark:text-purple-400">
                        Sử dụng ứng dụng quét QR từ điện thoại khác để test check-in
                      </div>
                      <div className="p-2 bg-white rounded-md">
                        <QRCodeGenerator code={qrValue} size={200} />
                      </div>
                      <div className="mt-2 text-center text-xs text-gray-500 dark:text-gray-400">
                        Mã QR: {qrValue}
                      </div>
                    </motion.div>
                  ) : (
                    <div className="mt-2 py-5 flex justify-center bg-purple-50/50 dark:bg-purple-900/20 rounded-md border border-purple-100/50 dark:border-purple-800/30 shadow-sm" onClick={toggleQrCodeVisibility}>
                      <span className="text-purple-500 dark:text-purple-400 flex items-center gap-1.5 text-sm animate-pulse">
                        <QrCode size={18} /> Nhấn để xem mã QR để test check-in
                      </span>
                    </div>
                  )}
                </div>
                
                {showContribution && (
                  <div className="flex flex-col mt-1 py-2 border-t border-slate-200/70 dark:border-slate-700/70">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600 dark:text-slate-400 font-medium flex items-center gap-1.5">
                        <TrendingUp size={14} className="text-amber-500" /> 
                        Đóng góp:
                      </span>
                      <div className="flex items-center">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="h-10 px-4 ml-1 rounded-full bg-amber-50 hover:bg-amber-100 border-amber-200 text-amber-700 
                            dark:bg-amber-900/30 dark:hover:bg-amber-800/40 dark:border-amber-700/50 dark:text-amber-400
                            shadow-sm flex items-center gap-1.5 z-10 touch-target"
                          onClick={toggleContributionVisibility}
                          title={showContributionData ? "Ẩn thông tin đóng góp" : "Hiển thị thông tin đóng góp"}
                        >
                          {showContributionData ? (
                            <>
                              <EyeOff size={14} />
                              <span className="text-xs ml-1">Ẩn</span>
                            </>
                          ) : (
                            <>
                              <Eye size={14} className="animate-pulse" />
                              <span className="text-xs ml-1">Xem</span>
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                    
                    {showContributionData ? (
                      <motion.div 
                        className="mt-2 px-3 py-3 bg-amber-50/80 dark:bg-amber-900/20 rounded-md border border-amber-100 dark:border-amber-800/30 shadow-sm"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="flex justify-center">
                          <div className={`text-xl font-bold ${attendee.contribution ? "text-amber-600 dark:text-amber-400" : "text-slate-400 italic"} px-2.5 py-1 bg-white/70 dark:bg-gray-800/30 rounded-full shadow-sm border border-amber-100 dark:border-amber-800/20`}>
                            {attendee.contribution ? attendee.contribution.toLocaleString('vi-VN') + " VNĐ" : "Chưa đóng góp"}
                          </div>
                        </div>
                      </motion.div>
                    ) : (
                      <div className="mt-2 py-5 flex justify-center bg-amber-50/50 rounded-md border border-amber-100/50 shadow-sm" onClick={toggleContributionVisibility}>
                        <span className="text-amber-500 dark:text-amber-400 flex items-center gap-1.5 text-sm animate-pulse">
                          <Eye size={18} /> Nhấn để xem thông tin đóng góp
                        </span>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="flex items-center justify-between mt-1 pt-2 border-t border-slate-200/70 dark:border-slate-700/70">
                  <span className="text-slate-600 dark:text-slate-400 font-medium">Thanh toán:</span>
                  <PaymentToggle 
                    attendee={{
                      ...attendee,
                      t_shirt_size: displayTShirtSize
                    }}
                    isPaid={isPaid}
                    isUpdating={isUpdating}
                    onPaymentToggleRequest={handlePaymentToggleRequest}
                  />
                </div>
              </CardContent>
            </motion.div>
          </CollapsibleContent>
        </Collapsible>
        
        <div className="flex justify-center border-t border-slate-200/70 dark:border-slate-700/70">
          <Button 
            variant="ghost" 
            size="sm" 
            className="p-0 h-10 w-full rounded-none hover:bg-white/50 dark:hover:bg-gray-800/50 text-slate-600 dark:text-slate-400 transition-colors duration-200 touch-target"
            onClick={onToggleExpand}
          >
            {isExpanded ? 
              <ChevronUp size={20} className="text-slate-500" /> : 
              <ChevronDown size={20} className="text-slate-500" />
            }
          </Button>
        </div>
      </Card>
    </motion.div>
  );
};

export default AttendeeCard;
